import {useEffect,useRef,useState} from 'react';
import type {PDFDocumentProxy,RenderTask} from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/** Render retained bytes as inert pages, with document scrolling and no embedded viewer. */
export function PdfReadingPages({url,fileName}:{url:string;fileName:string}){
 const [pdf,setPdf]=useState<PDFDocumentProxy>(),[failure,setFailure]=useState(''),[page,setPage]=useState(1);
 const [width,setWidth]=useState(0),[rendered,setRendered]=useState(false),[pageText,setPageText]=useState('');
 const holder=useRef<HTMLDivElement>(null),canvas=useRef<HTMLCanvasElement>(null);
 useEffect(()=>{
  const element=holder.current;if(!element)return;
  const measure=()=>setWidth(Math.max(1,Math.floor(element.getBoundingClientRect().width)));
  measure();const observer=new ResizeObserver(measure);observer.observe(element);return()=>observer.disconnect();
 },[]);
 useEffect(()=>{
  let active=true,release=()=>{};setPdf(undefined);setFailure('');setPage(1);setRendered(false);setPageText('');
  void (async()=>{
   try{
    if(!url.startsWith('blob:'))throw Error('The authenticated original is unavailable.');
    const response=await fetch(url);if(!response.ok)throw Error('The retained PDF could not be read.');
    const data=new Uint8Array(await response.arrayBuffer());if(!active)return;
    const library=await import('pdfjs-dist');if(!active)return;
    library.GlobalWorkerOptions.workerSrc=workerUrl;
    const wasmUrl=new URL(`/pdfjs/${library.version}/`,window.location.origin).href;
    // Retain decoder warnings for diagnosis; incomplete pages still fail closed below.
    const task=library.getDocument({data,wasmUrl,useSystemFonts:true,useWasm:false,stopAtErrors:true,enableXfa:false,disableAutoFetch:true,verbosity:1});
    release=()=>{void task.destroy().catch(()=>{})};
    const document=await task.promise;
    if(!active){release();return;}setPdf(document);
   }catch{if(active)setFailure('This PDF could not be previewed here. Open the retained original in the separate browser viewer.');}
  })();
  return()=>{active=false;release()};
 },[url]);
 const current=pdf?Math.min(page,pdf.numPages):1;
 useEffect(()=>{
  const element=canvas.current;if(!pdf||!element||!width)return;
  let active=true,task:RenderTask|undefined;const imageWait=new AbortController();setRendered(false);setFailure('');setPageText('');
  element.getContext('2d')?.clearRect(0,0,element.width,element.height);
  void (async()=>{
   const original=await pdf.getPage(current);
   try{
    if(!active)return;const base=original.getViewport({scale:1});
    const density=Math.min(window.devicePixelRatio||1,1.5),scale=Math.min(width/base.width*density,Math.sqrt(4_000_000/(base.width*base.height)));
    const viewport=original.getViewport({scale});element.width=Math.max(1,Math.ceil(viewport.width));element.height=Math.max(1,Math.ceil(viewport.height));
    task=original.render({canvas:element,viewport});await task.promise;if(!active)return;
    // PDF.js can resolve a failed external image as null even with stopAtErrors.
    // Do not present the surviving page frame as a fully rendered original.
    const {OPS}=await import('pdfjs-dist'),operators=await original.getOperatorList();
    for(let index=0;index<operators.fnArray.length;index++){
     if(operators.fnArray[index]!==OPS.paintImageXObject&&operators.fnArray[index]!==OPS.paintImageXObjectRepeat)continue;
     const id=operators.argsArray[index][0] as string,objects=id.startsWith('g_')?original.commonObjs:original.objs;
     // The operator-list intent can finish before its asynchronous image decode.
     // Wait for the resolved object; a decoder failure still resolves as null.
     const image=await new Promise<unknown>((resolve,reject)=>{
      const finish=(value:unknown,error?:Error)=>{clearTimeout(timer);imageWait.signal.removeEventListener('abort',cancel);error?reject(error):resolve(value)};
      const cancel=()=>finish(undefined,Error('The page preview was cancelled.'));
      const timer=setTimeout(()=>finish(undefined,Error('A retained page image did not finish decoding.')),15_000);
      if(imageWait.signal.aborted){cancel();return;}
      imageWait.signal.addEventListener('abort',cancel,{once:true});
      try{objects.get(id,(value:unknown)=>finish(value))}catch(error){finish(undefined,error instanceof Error?error:Error('A retained page image is unavailable.'))}
     });
     if(!image)throw Error('A retained page image could not be decoded.');
    }
    if(!active)return;
    setRendered(true);const text=await original.getTextContent();
    if(active)setPageText(text.items.map(item=>'str' in item?item.str:'').join(' '));
   }finally{original.cleanup()}
  })().catch(()=>{if(active)setFailure('This page could not be rendered. Open the retained original to review it in the separate viewer.');});
  return()=>{active=false;imageWait.abort();task?.cancel()};
 },[pdf,current,width]);
 return <section className="pdf-reading-pages" aria-label={'PDF preview of '+fileName} aria-busy={!failure&&!rendered}>
  <div className="pdf-reading-toolbar">
   {pdf&&<><button type="button" className="secondary" disabled={current===1||(!rendered&&!failure)} onClick={()=>{setRendered(false);setPageText('');setPage(value=>value-1)}}>Previous PDF page</button>
    <span aria-live="polite">Page {current} of {pdf.numPages}</span>
    <button type="button" className="secondary" disabled={current===pdf.numPages||(!rendered&&!failure)} onClick={()=>{setRendered(false);setPageText('');setPage(value=>value+1)}}>Next PDF page</button></>}
   <a href={url} target="_blank" rel="noopener noreferrer">Open PDF in browser</a>
  </div>
  {failure?<p role="alert">{failure}</p>:!rendered&&<p role="status">Preparing PDF page…</p>}
  <div className="pdf-reading-page" ref={holder}>
   <canvas ref={canvas} style={{display:failure?'none':undefined}} role="img" aria-label={`Retained PDF page ${current}${pdf?' of '+pdf.numPages:''}`} aria-hidden={!rendered||Boolean(failure)}/>
  </div>
  {rendered&&pageText&&<details className="pdf-page-text"><summary>Read text from this page</summary><p>{pageText}</p></details>}
 </section>;
}
